import {
  BroadcastIcon,
  FullScreenExitIcon,
  FullScreenIcon,
  MenuDropdown,
} from '@hakimo-ui/hakimo/ui-elements';
import { FilmIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAlarmGroupCamera } from '../alarm-provider/AlarmGroupProvider';
import { CameraFeedMode } from '../types';
import CameraTimeline from './CameraTimeline';

interface Props {
  cameraId: string;
  mode: CameraFeedMode;
  isFullScreen: boolean;
  toggleFullscreen: () => void;
  onChangeCamFeedMode: (val: CameraFeedMode) => void;
}

export function CamMonitorControls(props: Props) {
  const {
    mode,
    isFullScreen,
    toggleFullscreen,
    cameraId,
    onChangeCamFeedMode,
  } = props;

  const { camToDetailsMap, extraCamDetailsMap } = useAlarmGroupCamera();
  const cameraDetails =
    camToDetailsMap[cameraId] ?? extraCamDetailsMap[cameraId];
  if (!cameraDetails) {
    return null;
  }
  const { cameraName } = cameraDetails;

  const menuButton =
    mode === CameraFeedMode.ALARM_VIDEO ? (
      <FilmIcon className="text-ondark-text-1 h-4 w-4 rounded-lg fill-white" />
    ) : (
      <div className="flex animate-pulse items-center gap-1 text-red-500">
        <BroadcastIcon
          className={clsx(
            'h-4 w-4 rounded-lg fill-white',
            mode === CameraFeedMode.LIVE ? 'bg-red-500' : 'bg-gray-500'
          )}
        />
        <span className="text-xs">Live</span>
      </div>
    );

  const getCamFeedMenuItems = (
    currentVal: CameraFeedMode,
    onChange: (val: CameraFeedMode) => void
  ) => {
    const onClick = (type: CameraFeedMode) => () => onChange(type);
    const className = 'text-primary-500 dark:text-primary-500';

    return [
      {
        name: 'LIVE',
        onClick: onClick(CameraFeedMode.LIVE),
        className:
          currentVal === CameraFeedMode.LIVE ? className : 'text-ondark-text-1',
        icon: (
          <BroadcastIcon className="h-4 w-4 animate-pulse rounded-lg bg-red-500 fill-white" />
        ),
      },
      {
        name: 'Event Video',
        onClick: onClick(CameraFeedMode.ALARM_VIDEO),
        className:
          currentVal === CameraFeedMode.ALARM_VIDEO
            ? className
            : 'text-ondark-text-1',
        icon: <FilmIcon className="h-4 w-4" />,
      },
    ];
  };

  return (
    <div
      className={clsx(
        'from-dark-surface absolute bottom-0 left-0 right-0 cursor-pointer rounded-md bg-gradient-to-t',
        mode === CameraFeedMode.SPLIT ? 'right-[50.5%]' : 'right-0'
      )}
    >
      {(mode === CameraFeedMode.ALARM_VIDEO ||
        mode === CameraFeedMode.SPLIT) && (
        <CameraTimeline
          cameraId={cameraId}
          showSeekerIndicator
          showAlarmGroupBounds
        />
      )}
      {mode !== CameraFeedMode.SPLIT && (
        <div className="flex items-center justify-between px-1 py-[1px]">
          <MenuDropdown
            position="top"
            items={getCamFeedMenuItems(mode, onChangeCamFeedMode)}
            menuButton={menuButton}
            isMinimized
            theme="dark"
          />
          <span
            title={cameraName}
            className="truncate text-xs"
            style={{ color: '#40C4FF' }}
          >
            {cameraName}
          </span>
          <button
            onClick={toggleFullscreen}
            className="rounded-md focus:ring-offset-0 enabled:hover:bg-slate-800/50"
          >
            {isFullScreen ? (
              <FullScreenExitIcon className="fill-dark-text h-5 w-5" />
            ) : (
              <FullScreenIcon className="fill-dark-text h-5 w-5" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default CamMonitorControls;
